import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Superadmin } from "../../shared/types";
import { api, ErrorApi } from "../lib/apiClient";

// Deliberadamente un contexto y un estado completamente separados de
// AuthContext (tenant) — nunca comparten sesión, cookie ni almacenamiento.
interface ContextoAuthSuperadmin {
  superadmin: Superadmin | null;
  cargandoInicial: boolean;
  iniciarSesion: (email: string, password: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
}

const ContextoAuthSuperadminReact =
  createContext<ContextoAuthSuperadmin | null>(null);

export function ProveedorAuthSuperadmin({
  children,
}: {
  children: ReactNode;
}) {
  const [superadmin, setSuperadmin] = useState<Superadmin | null>(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);

  useEffect(() => {
    api
      .get<{ superadmin: Superadmin }>("/superadmin/auth/me")
      .then((datos) => setSuperadmin(datos.superadmin))
      .catch(() => setSuperadmin(null))
      .finally(() => setCargandoInicial(false));
  }, []);

  const iniciarSesion = useCallback(async (email: string, password: string) => {
    const datos = await api.post<{ superadmin: Superadmin }>(
      "/superadmin/auth/login",
      { email, password },
    );
    setSuperadmin(datos.superadmin);
  }, []);

  const cerrarSesion = useCallback(async () => {
    try {
      await api.post("/superadmin/auth/logout");
    } catch (error) {
      if (!(error instanceof ErrorApi)) throw error;
    }
    setSuperadmin(null);
  }, []);

  return (
    <ContextoAuthSuperadminReact.Provider
      value={{ superadmin, cargandoInicial, iniciarSesion, cerrarSesion }}
    >
      {children}
    </ContextoAuthSuperadminReact.Provider>
  );
}

export function useAuthSuperadmin(): ContextoAuthSuperadmin {
  const contexto = useContext(ContextoAuthSuperadminReact);
  if (!contexto)
    throw new Error(
      "useAuthSuperadmin debe usarse dentro de ProveedorAuthSuperadmin",
    );
  return contexto;
}
